import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useStreamCategories } from "./useStreamCategories";
import { useStreamBracket } from "./useStreamBracket";
import { useTransparentBody } from "./useTransparentBody";
import StreamControls from "./StreamControls";

// Page overlay stream — liste de départ (fond transparent, à utiliser
// comme source navigateur OBS). URL : /stream/startlist?category=women1
// (&round=N pour figer un tour précis, &hide=1 pour masquer le sélecteur
// une fois la source configurée).

const COLOR_HEX = { blue: "#1976d2", red: "#d32f2f" };

// Sans ?round= explicite, affiche automatiquement : le tour du duel actif
// s'il y en a un, sinon le premier tour qui a encore un duel non tranché
// (avec ses 2 athlètes réels, pas un BYE) — à défaut, le dernier tour.
function defaultRoundIndex(bracket) {
    if (!bracket?.rounds?.length) return -1;
    if (bracket.activeMatchId) {
        const idx = bracket.rounds.findIndex((r) => r.some((m) => m.id === bracket.activeMatchId));
        if (idx !== -1) return idx;
    }
    const idx = bracket.rounds.findIndex((r) =>
        r.some((m) => !m.winner && m.athleteA && m.athleteB && !m.byeA && !m.byeB)
    );
    return idx !== -1 ? idx : bracket.rounds.length - 1;
}

function AthleteLine({ athlete, color }) {
    if (!athlete) return <div style={rowStyle}>—</div>;
    if (athlete.bye) return <div style={{ ...rowStyle, opacity: 0.5 }}>BYE</div>;
    return (
        <div style={rowStyle}>
            <span style={{ ...badgeStyle, background: COLOR_HEX[color] || "#555" }}>{athlete.bib ?? ""}</span>
            <span style={nameStyle}>{athlete.name}</span>
            {athlete.qualifRank ? <span style={rankStyle}>#{athlete.qualifRank}</span> : null}
        </div>
    );
}

const rowStyle = { display: "flex", alignItems: "center", gap: 10, padding: "4px 0" };
const badgeStyle = {
    minWidth: 34,
    textAlign: "center",
    padding: "2px 8px",
    borderRadius: 6,
    color: "#fff",
    fontWeight: 700,
    fontSize: 20,
};
const nameStyle = { fontSize: 24, fontWeight: 700, color: "#fff", textShadow: "0 2px 4px rgba(0,0,0,0.8)" };
const rankStyle = { fontSize: 14, color: "#ccc" };

export default function StreamStartList() {
    useTransparentBody();
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const hidden = params.get("hide") === "1";
    const roundParam = params.get("round");

    const categories = useStreamCategories();
    const bracket = useStreamBracket(category);

    const roundIndex = useMemo(() => {
        if (!bracket) return -1;
        if (roundParam === "small") return "small";
        if (roundParam !== null && bracket.rounds[Number(roundParam)]) return Number(roundParam);
        return defaultRoundIndex(bracket);
    }, [bracket, roundParam]);

    const round =
        roundIndex === "small" ? (bracket?.smallFinal ? [bracket.smallFinal] : []) : bracket?.rounds?.[roundIndex] || [];
    const roundLabel = roundIndex === "small" ? "Petite finale (3e / 4e)" : round[0]?.label || "";

    const setCategory = (value) => {
        const next = new URLSearchParams(params);
        if (value) next.set("category", value);
        else next.delete("category");
        next.delete("round"); // repart en "auto" sur une nouvelle course
        setParams(next, { replace: true });
    };

    const setRound = (value) => {
        const next = new URLSearchParams(params);
        if (value === "") next.delete("round");
        else next.set("round", value);
        setParams(next, { replace: true });
    };

    return (
        <div style={{ minHeight: "100vh", background: "transparent", padding: 24, fontFamily: "sans-serif" }}>
            <StreamControls categories={categories} category={category} onCategoryChange={setCategory} hidden={hidden}>
                {bracket && (
                    <select value={roundParam ?? ""} onChange={(e) => setRound(e.target.value)} style={{ fontSize: 13 }}>
                        <option value="">Auto (tour en cours)</option>
                        {bracket.rounds.map((r, i) => (
                            <option key={i} value={i}>
                                {r[0]?.label || `Tour ${i + 1}`}
                            </option>
                        ))}
                        {bracket.smallFinal && <option value="small">Petite finale</option>}
                    </select>
                )}
            </StreamControls>

            {!category && !hidden && <div style={{ color: "#fff" }}>Choisissez une course ci-dessus.</div>}

            {bracket && round.length > 0 && (
                <div style={{ maxWidth: 640 }}>
                    <div
                        style={{
                            fontSize: 30,
                            fontWeight: 800,
                            color: "#fff",
                            textShadow: "0 2px 6px rgba(0,0,0,0.85)",
                            marginBottom: 16,
                        }}
                    >
                        {roundLabel}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        {round.map((duel) => (
                            <div
                                key={duel.id}
                                style={{
                                    background: "rgba(0,0,0,0.55)",
                                    borderRadius: 10,
                                    padding: "10px 16px",
                                    borderLeft: duel.winner ? "5px solid #2e7d32" : "5px solid transparent",
                                }}
                            >
                                <AthleteLine athlete={duel.athleteA} color={duel.colorA} />
                                <AthleteLine athlete={duel.athleteB} color={duel.colorB} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
